<template>
  <!-- Kit SessionRow (helpers in kit/sessionRow.js):
         home     the Home board's 72 px row (56 compact): the owner's tile,
                  the title with the Live badge, "8 loops · you, Maya, Theo
                  · now" with a globe for public sessions, an inset rule
         list     the list column's 58 px row: title and time, then Live
                  and "8 loops · you, Maya, Theo"
         profile  the Profile board's 68 px row: tile 44, "4 loops ·
                  public · last month", a trailing slot for its play button
       Emits open (a click, Enter) and menu (right-click, long-press). -->
  <div
    class="session-row"
    :class="[
      `session-row--${variant}`,
      { 'is-selected': selected, 'is-highlighted': highlighted, 'is-compact': compact },
    ]"
    role="button"
    tabindex="0"
    :aria-current="selected ? 'true' : null"
    :aria-label="title"
    @click="$emit('open', $event)"
    @keydown.enter.prevent="$emit('open', $event)"
    @contextmenu.prevent="$emit('menu', $event)"
    @touchstart.passive="pressStart"
    @touchend="pressEnd"
    @touchmove.passive="pressEnd"
  >
    <template v-if="variant === 'list'">
      <div class="sr-line1">
        <span class="sr-title">{{ title }}</span>
        <span class="sr-when">{{ when }}</span>
      </div>
      <div class="sr-line2">
        <template v-if="live">
          <span class="sr-dot" aria-hidden="true" />
          <span class="sr-live">Live</span>
        </template>
        <span class="sr-sub">{{ listSub }}</span>
      </div>
    </template>

    <template v-else>
      <div class="sr-tile" :style="tileStyle">
        <img v-if="picture" :src="picture" alt="" class="sr-tile-img" @error="pictureFailed = true" />
        <svg v-else width="30" height="22" viewBox="0 0 30 22" aria-hidden="true">
          <path :d="glyph" :stroke="color" stroke-width="2.2" stroke-linecap="round" fill="none" />
        </svg>
      </div>
      <div class="sr-body">
        <div class="sr-text">
          <div class="sr-title-row">
            <span class="sr-title">{{ title }}</span>
            <span v-if="live && variant === 'home'" class="sr-badge">
              <span class="sr-dot" aria-hidden="true" />{{ badgeText }}
            </span>
          </div>
          <div class="sr-sub-row">
            <svg v-if="isPublic && variant === 'home'" class="sr-globe" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-label="Public">
              <circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
            </svg>
            <span class="sr-sub">{{ variant === "profile" ? profileSub : homeSub }}</span>
          </div>
        </div>
        <div v-if="busy || $slots.trailing" class="sr-trailing" @click.stop>
          <v-progress-circular v-if="busy" indeterminate size="32" width="2" color="var(--cl-accent-text)" />
          <slot v-else name="trailing" />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  glyphPath,
  isPrivate,
  listSubtitle,
  liveBadgeText,
  ownerColor,
  profileSubtitle,
  rowTitle,
  sessionIsLive,
  sessionSubtitle,
  shortWhen,
} from "@/kit/sessionRow.js";

export default {
  name: "SessionRow",
  props: {
    session: { type: Object, required: true },
    me: { type: String, default: null },
    variant: {
      type: String,
      default: "home",
      validator: (v) => ["list", "home", "profile"].includes(v),
    },
    selected: { type: Boolean, default: false },
    highlighted: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
    // The clock behind ages and presence (tests, the kit gallery).
    now: { type: Date, default: null },
  },
  emits: ["open", "menu"],
  data: () => ({ pictureFailed: false }),
  computed: {
    title() {
      return rowTitle(this.session);
    },
    color() {
      return ownerColor(this.session, this.me);
    },
    glyph() {
      return glyphPath(this.session.name || this.session.creator || "");
    },
    picture() {
      const p = this.session.picture;
      return !this.pictureFailed && typeof p === "string" && p.startsWith("http") ? p : null;
    },
    tileStyle() {
      return { background: `color-mix(in srgb, ${this.color} 16%, transparent)` };
    },
    live() {
      return sessionIsLive(this.session, this.me, this.now || undefined);
    },
    badgeText() {
      return liveBadgeText(this.session, this.now || undefined);
    },
    isPublic() {
      return !isPrivate(this.session);
    },
    homeSub() {
      return sessionSubtitle(this.session, this.me, this.now || undefined);
    },
    listSub() {
      return listSubtitle(this.session, this.me);
    },
    profileSub() {
      return profileSubtitle(this.session, this.me, this.now || undefined);
    },
    when() {
      return shortWhen(this.session.created_at, this.now || undefined);
    },
  },
  watch: {
    "session.picture"() {
      this.pictureFailed = false;
    },
  },
  methods: {
    // A long press opens the row's menu, as a right-click does.
    pressStart(e) {
      clearTimeout(this.pressTimer);
      const t = e.touches && e.touches[0];
      this.pressTimer = setTimeout(() => {
        this.$emit("menu", { clientX: t ? t.clientX : 0, clientY: t ? t.clientY : 0, preventDefault() {} });
      }, 550);
    },
    pressEnd() {
      clearTimeout(this.pressTimer);
    },
  },
};
</script>

<style scoped>
.session-row {
  display: flex;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  outline: none;
  color: var(--cl-label);
}
.session-row:focus-visible {
  box-shadow: inset 0 0 0 2px var(--cl-accent);
}

/* list (the column beside an open session) */
.session-row--list {
  height: 58px;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
  padding: 0 10px;
  border-radius: 8px;
  flex-shrink: 0;
}
@media (hover: hover) {
  .session-row--list:hover {
    background: var(--cl-hover);
  }
}
.session-row--list.is-selected {
  background: var(--cl-accent-fill);
}
.sr-line1,
.sr-line2 {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.session-row--list .sr-title {
  flex: 1;
  font: 600 13px/16px var(--cl-font);
}
.sr-when {
  flex-shrink: 0;
  font: 400 11px/14px var(--cl-font);
  color: var(--cl-label-3);
}
.sr-line2 {
  font: 400 12px/15px var(--cl-font);
  color: var(--cl-label-2);
}
.sr-live {
  color: var(--cl-live-text);
  font-weight: 600;
}
.sr-dot {
  width: 6px;
  height: 6px;
  border-radius: 3px;
  background: var(--cl-live);
  flex-shrink: 0;
}

/* home and profile */
.session-row--home,
.session-row--profile {
  align-items: center;
  gap: 12px;
  padding-left: 16px;
  background: var(--cl-bg);
}
.session-row--home {
  height: 72px;
}
.session-row--home.is-compact {
  height: 56px;
}
.session-row--profile {
  height: 68px;
}
.session-row--home.is-highlighted {
  background: var(--cl-control-fill-pointer);
}
.session-row--home.is-selected {
  background: var(--cl-accent-fill);
}
.sr-tile {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.is-compact .sr-tile {
  width: 40px;
  height: 40px;
}
.session-row--profile .sr-tile {
  width: 44px;
  height: 44px;
  border-radius: 11px;
}
.sr-tile-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.sr-body {
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 16px;
  border-bottom: 1px solid var(--cl-separator);
}
.sr-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.is-compact .sr-text {
  gap: 1px;
}
.sr-title-row,
.sr-sub-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.sr-sub-row {
  gap: 6px;
  color: var(--cl-label-2);
}
.sr-title {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font: var(--cl-headline);
}
.sr-sub {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font: var(--cl-subhead);
  color: var(--cl-label-2);
}
/* Profile's rows: a 17/600 title over a 14 px sub, each at the font's own
   line height, nothing between them. */
.session-row--profile .sr-text {
  gap: 0;
}
.session-row--profile .sr-title {
  font: 600 17px var(--cl-font);
}
.session-row--profile .sr-sub {
  font: 400 14px var(--cl-font);
}
.sr-globe {
  flex-shrink: 0;
}
.sr-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  height: 20px;
  padding: 0 8px;
  border-radius: 10px;
  background: var(--cl-live-fill);
  color: var(--cl-live-text);
  font: 600 12px/1 var(--cl-font);
}
.sr-trailing {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
</style>
